import CoinUpArrow from "@icons/coin-up-arrow.svg?react";
import CrossSvg from "@icons/cross.svg?react";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import invariant from "tiny-invariant";

import { ClientError } from "@/api/errors";
import { Chart } from "@/common/components/chart/chart";
import { Failed, NoData, Pending } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { PageTitle } from "@/common/components/page-title/page-title";
import { QuickFilters } from "@/common/components/quick-filters/quick-filters";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useToggle } from "@/common/hooks/use-toggle";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { DummyRatesPeriod } from "@/domain/plan/stock-exchange/stock-exchange.dummy";
import {
  isMaskableCommodity,
  MaskableCommodity,
  Mode,
  StockExchangeFlow,
} from "@/domain/plan/stock-exchange/stock-exchange.flow";
import { CurrencyRatesToCurrency1 } from "@/generated/api/prices";

import { TwoColumnLayout } from "../layout/layout";
import { LockedScreen } from "../shared/components/locked-screen/locked-screen";
import { CallToAction } from "./components/call-to-action";
import { CommodityOutlookQueryResult } from "./stock-exchange.api";
import {
  CommodityProvider,
  CurrencyProvider,
  Filters,
} from "./stock-exchange.chart";
import { Outlook, OutlookList } from "./stock-exchange.outlook";
import {
  initialParams,
  mapping,
  stockExchangeParamsSchema,
} from "./stock-exchange.utils";

const path = "stock-exchange";
const charts = [
  {
    commodity: "WHEAT",
    currency: "EUR",
    periods: ["CURRENT", "NEXT"],
    source: "MATIF",
    unit: "/t",
  },
  {
    commodity: "RAPESEED",
    currency: "EUR",
    periods: ["CURRENT", "NEXT"],
    source: "MATIF",
    unit: "/t",
  },
  {
    commodity: "CRUDE_OIL",
    currency: "USD",
    periods: ["CURRENT"],
    source: "BRENT",
    unit: "/barel",
  },
] as const;

const MaskedChart = ({
  commodity,
  currency,
  outlook,
  rates,
  source,
  unit,
}: {
  commodity: MaskableCommodity;
  currency: CurrencyRatesToCurrency1;
  outlook: CommodityOutlookQueryResult | undefined;
  rates: DummyRatesPeriod[] | undefined;
  source: string;
  unit: string;
}) => {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(rates, "Dummy rates missing for masked chart");
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(outlook, "Dummy outlook missing for masked chart");

  return (
    <div aria-hidden className="blur-xs">
      <div className="mr-6 flex flex-wrap justify-between gap-y-1">
        <Chart.Price>
          {rates[0].current}
          {unit}
        </Chart.Price>
        <Chart.Trend currency={currency}>{rates[0].trend}</Chart.Trend>
        <Chart.Source>
          <Trans>Zdroj</Trans>: {source}
        </Chart.Source>
      </div>
      <Chart
        currency={currency}
        data={{ current: rates[0].rates, next: rates[1]?.rates }}
        data-testid={`chart-${commodity.toLowerCase()}`}
      />
      <OutlookList data={outlook} />
    </div>
  );
};

const LiveChart = ({ chart }: { chart: (typeof charts)[number] }) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <>
            <Failed error={error}>
              <button
                className="text-can-forest-teal text-xs underline"
                onClick={resetErrorBoundary}
              >
                <Trans>Zkusit znovu</Trans>
              </button>
            </Failed>
          </>
        )}
        onReset={reset}
      >
        <Suspense fallback={<Spinner />}>
          <CommodityProvider
            commodityId={chart.commodity}
            currency={chart.currency}
            key={chart.commodity}
            periods={chart.periods}
          >
            {({ data, status }) => (
              <>
                {status === "success" ? (
                  <>
                    <div className="mr-6 flex flex-wrap justify-between gap-y-1">
                      <Chart.Price>
                        {data[0].current}
                        {chart.unit}
                      </Chart.Price>
                      {data[0].trend ? (
                        <Chart.Trend currency={chart.currency}>
                          {data[0].trend}
                        </Chart.Trend>
                      ) : null}
                      <Chart.Source>
                        <Trans>Zdroj</Trans>: {chart.source}
                      </Chart.Source>
                    </div>
                    <Chart
                      currency={chart.currency}
                      data={{
                        current: data[0].rates,
                        next: data[1] ? data[1].rates : undefined,
                      }}
                      data-testid={`chart-${chart.commodity.toLowerCase()}`}
                      tooltip={
                        data[1]
                          ? (label, datasetIndex) => {
                              if (datasetIndex === 0) {
                                return (
                                  label +
                                  // eslint-disable-next-line lingui/no-unlocalized-strings
                                  "<span class='pl-2 font-normal'>(" +
                                  t`letošní sklizeň` +
                                  ")</span>"
                                );
                              }

                              return (
                                label +
                                // eslint-disable-next-line lingui/no-unlocalized-strings
                                "<span class='pl-2 font-normal'>(" +
                                t`budoucí sklizeň` +
                                ")</span>"
                              );
                            }
                          : undefined
                      }
                    />
                  </>
                ) : (
                  <NoData>
                    <Trans>Burza ještě neotevřela</Trans>
                  </NoData>
                )}
                {chart.commodity === "WHEAT" ||
                chart.commodity === "RAPESEED" ? (
                  <Outlook commodityId={chart.commodity} />
                ) : null}
              </>
            )}
          </CommodityProvider>
        </Suspense>
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
);

const StockExchange = ({ mode }: { mode: Mode }) => {
  const { _ } = useLingui();
  const sidebar = useSidebar();
  const showPackages = useToggle();
  const flow = React.useRef(new StockExchangeFlow());
  flow.current.setStrategy(mode);

  if (showPackages.on) {
    return (
      <LockedScreen>
        <button className="mt-2 mr-2 self-start" onClick={showPackages.setOff}>
          <CrossSvg />
        </button>
      </LockedScreen>
    );
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <>
            <div className="flex basis-full flex-col">
              <Metadata title={_(msg`Burza`)} />
              <div
                className={`flex flex-col gap-x-6 gap-y-8 md:mx-0 lg:grid ${sidebar.state.screen ? "lg:grid-cols-1 2xl:grid-cols-2" : "lg:grid-cols-2"}`}
              >
                {charts.map((chart) => {
                  return (
                    <Filters
                      commodityId={chart.commodity}
                      key={chart.commodity}
                    >
                      {(filters) => (
                        <Chart.Container>
                          <Chart.Title>
                            {mapping[chart.commodity].title}
                          </Chart.Title>
                          <QuickFilters {...filters} />
                          <Chart.ChartContainer>
                            {isMaskableCommodity(chart.commodity) &&
                            flow.current.getMasked(
                              chart.commodity,
                              filters.activeFilter.value,
                            ) ? (
                              <MaskedChart
                                commodity={chart.commodity}
                                currency={chart.currency}
                                outlook={flow.current.getDummyOutlook(
                                  chart.commodity,
                                )}
                                rates={flow.current.getDummyRates(
                                  chart.commodity,
                                  filters.activeFilter.value,
                                )}
                                source={chart.source}
                                unit={chart.unit}
                              />
                            ) : (
                              <LiveChart chart={chart} />
                            )}
                          </Chart.ChartContainer>
                        </Chart.Container>
                      )}
                    </Filters>
                  );
                })}

                <CurrencyProvider>
                  {({ currency, data, filters, status }) => (
                    <Chart.Container>
                      <Chart.Title>
                        <CoinUpArrow /> {data?.title}
                      </Chart.Title>
                      <QuickFilters {...filters} />
                      <Chart.ChartContainer>
                        {status === "success" ? (
                          <>
                            <div className="mr-6 flex flex-wrap justify-between gap-y-1">
                              <Chart.Price>{data.current}</Chart.Price>
                              <Chart.Trend currency={currency}>
                                {data.trend}
                              </Chart.Trend>
                              <Chart.Source>
                                <Trans>Zdroj</Trans>:{" "}
                                <Trans context="European central bank">
                                  ECB
                                </Trans>
                              </Chart.Source>
                            </div>
                            {data.rates.length > 0 ? (
                              <Chart
                                currency={currency}
                                data={{
                                  current: data.rates,
                                }}
                              />
                            ) : (
                              <NoData />
                            )}
                          </>
                        ) : status === "pending" ? (
                          <Pending />
                        ) : status === "empty" ? (
                          <NoData />
                        ) : (
                          <Failed error={new ClientError()} />
                        )}
                      </Chart.ChartContainer>
                    </Chart.Container>
                  )}
                </CurrencyProvider>
              </div>
              <Spacer />
            </div>
            {flow.current.showCallToAction() ? (
              <CallToAction onClick={showPackages.setOn} />
            ) : null}
          </>
        ),
        header: (
          <PageTitle>
            <Trans>Burza</Trans>
          </PageTitle>
        ),
      }}
      right={false}
    ></TwoColumnLayout>
  );
};

const StockExchangeParams = ({ children }: React.PropsWithChildren) => {
  const searchParams = useStockExchangeParams();
  const { pathname } = useLocation();

  if (!searchParams) {
    return <Navigate to={`${pathname}?${initialParams}`} />;
  }

  return <>{children}</>;
};

const useStockExchangeParams = () => {
  const [searchParams] = useSearchParams();
  const parsedParams = stockExchangeParamsSchema.safeParse(
    Object.fromEntries(searchParams),
  );

  if (parsedParams.success) {
    return parsedParams.data;
  }
};

export { path, StockExchange, StockExchangeParams, useStockExchangeParams };
